"use client";

import { useTranslations } from "@i18n/provider";
import { useLessonPreferences } from "@components/lesson/LessonPreferencesProvider";
import { formatRelativeTime } from "@lib/formatRelativeTime";
import type { TutorSessionSummary } from "@lib/agent/tutorChat/types";

type Props = {
  open: boolean;
  sessions: TutorSessionSummary[];
  activeSessionId: number | null;
  onSwitch: (id: number) => void;
};

// About four rows before the list scrolls, so a long history never squeezes the
// conversation underneath it.
const MAX_LIST_HEIGHT = 220;

export function TutorSessionsPanel({ open, sessions, activeSessionId, onSwitch }: Props) {
  const { t } = useTranslations();
  const { reducedMotion } = useLessonPreferences();

  return (
    // 0fr → 1fr animates to the list's natural height without measuring it, and
    // visibility (which transitions discretely) keeps the collapsed panel out of
    // the tab order and the accessibility tree once the animation lands.
    <div
      id="tutor-sessions-panel"
      aria-hidden={!open}
      className="grid flex-shrink-0"
      style={{
        gridTemplateRows: open ? "1fr" : "0fr",
        visibility: open ? "visible" : "hidden",
        transition: reducedMotion
          ? "none"
          : "grid-template-rows 260ms cubic-bezier(0.22, 1, 0.36, 1), visibility 260ms",
      }}
    >
      <div className="overflow-hidden">
        <div
          className="px-3 py-2"
          style={{ borderBottom: "1px solid var(--hairline)", background: "var(--surface-sunk)" }}
        >
          {sessions.length === 0 ? (
            <p className="px-3 py-2 text-xs" style={{ color: "var(--ink-faint)" }}>
              {t("agent.tutorSessions.empty")}
            </p>
          ) : (
            <ul
              className="flex flex-col gap-1 overflow-y-auto overscroll-contain"
              style={{ maxHeight: `${MAX_LIST_HEIGHT}px` }}
            >
              {sessions.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    onClick={() => onSwitch(s.id)}
                    aria-current={s.id === activeSessionId ? "true" : undefined}
                    className="flex min-h-[44px] w-full items-center justify-between gap-2 rounded-xl px-3 text-left transition-colors"
                    style={{
                      background: s.id === activeSessionId ? "var(--surface)" : "transparent",
                    }}
                  >
                    <span
                      className="min-w-0 flex-1 truncate text-sm"
                      style={{ color: "var(--ink-body)" }}
                    >
                      {s.title || s.preview || t("agent.tutorSessions.untitled")}
                    </span>
                    <span
                      className="flex-shrink-0 text-[10px]"
                      style={{ color: "var(--ink-faint)" }}
                    >
                      {formatRelativeTime(s.updatedAt)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
